import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { useTranslation } from "react-i18next";
import { CompassLogo } from "./CompassLogo";
import LanguageSwitcher from "./LanguageSwitcher";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import {
  LayoutDashboard,
  Users,
  Calendar,
  FileText,
  Globe2,
  Building2,
  HelpCircle,
  LogOut,
  Bell,
  UserCog,
  Coins,
  BadgeEuro,
  Heart,
  ClipboardCheck,
  Menu,
  X,
  Repeat2,
  Search,
  MessageSquare,
  LogIn,
  BookOpen,
  Award,
  Megaphone,
  ScrollText,
  Smartphone,
  Video,
  Archive,
  GraduationCap,
  UserPlus,
  Mail,
  ShieldAlert,
} from "lucide-react";

function buildNav(t, isChildLodge = false) {
  // The "Recognised Lodges" sidebar entry is context-aware: at Grand-Lodge
  // (obedience) level we use the dedicated `nav.recognizedGrandLodges`
  // key ("Recognised Grand Lodges"); at child-lodge level we fall back to
  // the generic `nav.recognizedLodges` label ("Recognised Lodges").
  const recognizedKey = isChildLodge
    ? t("nav.recognizedLodges", "Recognised Lodges")
    : t("nav.recognizedGrandLodges", "Recognised Grand Lodges");
  return [
    { to: "/", label: t("nav.dashboard"), icon: LayoutDashboard, end: true },
    { to: "/members", label: t("nav.members"), icon: Users },
    { to: "/global-members", label: t("nav.globalMembers"), icon: Search },
    { to: "/events", label: t("nav.events"), icon: Calendar },
    { to: "/announcements", label: t("nav.announcements", "Anunțuri"), icon: Megaphone, adminOnly: true },
    { to: "/attendance", label: t("nav.attendance"), icon: ClipboardCheck },
    { to: "/documents", label: t("nav.documents"), icon: FileText },
    { to: "/library", label: t("nav.library"), icon: BookOpen, adminOnly: true },
    { to: "/planse", label: t("nav.planse", "Planșe"), icon: ScrollText, adminOnly: true },
    { to: "/votes", label: t("nav.votes", "Voturi"), icon: Archive, adminOnly: true },
    { to: "/education", label: t("nav.education", "Educație"), icon: GraduationCap, adminOnly: true },
    { to: "/dues", label: t("nav.dues"), icon: Coins },
    ...(isChildLodge ? [] : [{ to: "/subscription", label: t("nav.subscription", "Abonament"), icon: BadgeEuro, adminOnly: true }]),
    { to: "/donations", label: t("nav.donations"), icon: Heart },
    { to: "/transfers", label: t("nav.transfers"), icon: Repeat2, adminOnly: true, divider: true },
    { to: "/diplomas", label: t("nav.diplomas"), icon: Award, adminOnly: true },
    { to: "/certificates", label: t("nav.certificates"), icon: ScrollText, adminOnly: true },
    { to: "/minutes", label: t("nav.minutes", "Procese-Verbale"), icon: ScrollText, adminOnly: true },
    { to: "/petitions", label: t("nav.petitions", "Petiție de afiliere"), icon: UserPlus, adminOnly: true },
    { to: "/recognized-lodges", label: recognizedKey, icon: Globe2, divider: true },
    { to: "/chat", label: t("nav.chat"), icon: MessageSquare },
    { to: "/mail", label: t("nav.mail", "E-mail"), icon: Mail, adminOnly: true },
    { to: "/meetings", label: t("nav.meetings", "Întâlniri online"), icon: Video, adminOnly: true },
    { to: "/lodge-info", label: t("nav.lodgeInfo"), icon: Building2 },
    { to: "/safety-reports", label: t("safetyReports.navLabel", "Raportări siguranță"), icon: ShieldAlert, adminOnly: true, divider: true },
    { to: "/support", label: t("nav.support"), icon: HelpCircle },
  ];
}

export default function AppShell() {
  const { t, i18n } = useTranslation();
  const { user, logout, activeChildLodge, exitChildLodge } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [lodge, setLodge] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [transfers, setTransfers] = useState({ incoming: [], outgoing: [] });
  const [transferInbox, setTransferInbox] = useState(false);
  const isAdmin = user?.role === "admin";
  // Limited officer accounts (Secretar/Trezorier/Mare Secretar/Mare
  // Trezorier) only see the modules ticked at account creation.
  const LIMITED_ROLES = ["secretary", "treasurer", "grand_secretary", "grand_treasurer"];
  const isLimitedOfficer = LIMITED_ROLES.includes(user?.office_role);
  const officerPerms = new Set(user?.permissions || []);
  const NAV_PERM_MAP = {
    "/members": ["members", "members_view"],
    "/events": ["events"],
    "/attendance": ["events"],
    "/announcements": ["announcements"],
    "/documents": ["documents"],
    "/votes": ["votes"],
    "/dues": ["dues"],
    "/donations": ["dues"],
    "/subscription": ["dues"],
    "/transfers": ["transfers"],
    "/diplomas": ["certificates"],
    "/certificates": ["certificates"],
    "/minutes": ["minutes"],
    "/petitions": ["petitions"],
    "/chat": ["messages"],
    "/mail": ["messages"],
  };
  const canSeeNav = (to) => {
    if (!isLimitedOfficer) return true;
    if (to === "/support") return true;
    const needed = NAV_PERM_MAP[to];
    return !!needed && needed.some((p) => officerPerms.has(p));
  };
  const NAV = buildNav(t, !!user?.child_lodge_id);

  // Limited officers: redirect away from any route outside their ticked
  // permissions (including the dashboard, which aggregates admin data).
  useEffect(() => {
    if (!isLimitedOfficer) return;
    const allowed = NAV.filter((it) => it.to !== "/" && canSeeNav(it.to)).map((it) => it.to);
    if (officerPerms.has("mobile_app")) allowed.push("/mobile-app");
    const path = location.pathname;
    const ok = allowed.some((p) => path === p || path.startsWith(p + "/"));
    if (!ok) navigate(allowed[0] || "/support", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLimitedOfficer, location.pathname]);

  const loadNotifs = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data);
    } catch (_e) {}
  };

  const loadLodge = async () => {
    try {
      const { data } = await api.get("/lodge");
      setLodge(data);
    } catch (_e) {}
  };

  const loadTransfers = async () => {
    if (!isAdmin || (isLimitedOfficer && !officerPerms.has("transfers"))) return;
    try {
      const [inc, out] = await Promise.all([
        api.get("/transfers/incoming"),
        api.get("/transfers/outgoing"),
      ]);
      setTransfers({ incoming: inc.data, outgoing: out.data });
    } catch (_e) {}
  };

  // "Nou" gold badge on the Dues sidebar link when there are unpaid
  // invoices for the current month (so treasurers see instantly there's
  // something to act on).
  const [hasPendingDuesThisMonth, setHasPendingDuesThisMonth] = useState(false);
  // Badge on the Petitions link: petitions submitted by profanes, waiting.
  const [pendingPetitions, setPendingPetitions] = useState(0);
  const loadPetitionsAlert = async () => {
    if (!isAdmin || (isLimitedOfficer && !officerPerms.has("petitions"))) { setPendingPetitions(0); return; }
    try {
      const { data } = await api.get("/petitions");
      setPendingPetitions(data?.pending_count || 0);
    } catch (_e) { setPendingPetitions(0); }
  };
  const loadDuesAlert = async () => {
    if (!isAdmin || (isLimitedOfficer && !officerPerms.has("dues"))) { setHasPendingDuesThisMonth(false); return; }
    try {
      const period = new Date().toISOString().slice(0, 7); // YYYY-MM
      const { data } = await api.get("/dues", { params: { period } });
      const rows = data?.dues || data || [];
      const pending = Array.isArray(rows)
        ? rows.some((d) => (d.status || "").toLowerCase() !== "paid" && !d.paid_at)
        : false;
      setHasPendingDuesThisMonth(pending);
    } catch (_e) { setHasPendingDuesThisMonth(false); }
  };

  useEffect(() => {
    loadNotifs();
    loadLodge();
    loadTransfers();
    loadDuesAlert();
    loadPetitionsAlert();
    setSidebarOpen(false);  // auto-close drawer on route change
    // Heartbeat for presence (every 60s).
    const beat = () => api.post("/presence/heartbeat").catch(() => {});
    beat();
    const tm = setInterval(() => { loadNotifs(); loadTransfers(); loadDuesAlert(); loadPetitionsAlert(); }, 30000);
    const heart = setInterval(beat, 60000);
    return () => { clearInterval(tm); clearInterval(heart); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Subscription pre-expiry banner (≤7 days left on the lodge/obedience
  // "paid until" date set by the SuperAdmin).
  const [subStatus, setSubStatus] = useState(null);
  useEffect(() => {
    if (!isAdmin || isLimitedOfficer) { setSubStatus(null); return; }
    api.get("/subscription/status").then(({ data }) => setSubStatus(data)).catch(() => {});
  }, [isAdmin, isLimitedOfficer]);

  const unread = notifs.filter((n) => !n.is_read).length;

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifs((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    } catch (_e) {}
  };

  return (
    <div className="app-shell flex">
      <Toaster position="bottom-right" toastOptions={{ style: { background: "#0d1b2a", color: "#e8c97a", border: "1px solid #c9a84c", fontFamily: "'Crimson Text', serif", borderRadius: 0 } }} />
      {/* Off-canvas backdrop for tablet/mobile */}
      {sidebarOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}
      <aside
        className={`sidebar w-64 shrink-0 flex flex-col sidebar-responsive${sidebarOpen ? " is-open" : ""}`}
        data-testid="sidebar"
      >
        <div className="px-6 py-7 flex flex-col items-center text-center">
          <CompassLogo size={64} color="#f5f0e8" />
          <h1 className="lodge-brand">SQUARE<br />& COMPASS</h1>
          <div className="lodge-brand-sub">Lodge Management</div>
        </div>

        <div className="gold-divider mx-6" />

        <nav className="mt-5 flex-1 overflow-y-auto">
          {NAV.filter((it) => (!it.adminOnly || isAdmin) && canSeeNav(it.to)).map(({ to, label, icon: Icon, end, divider }, idx) => (
            <React.Fragment key={to}>
              {divider && <div className="gold-divider mx-6 my-3" />}
              <NavLink
                to={to}
                end={end}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon size={16} strokeWidth={1.6} />
                <span>{label}</span>
                {to === "/dues" && hasPendingDuesThisMonth && (
                  <span
                    className="sidebar-new-badge"
                    data-testid="dues-new-badge"
                    title={t("dues.newNoticesThisMonthTitle", "Sunt notificări neachitate pentru luna curentă")}
                    style={{
                      marginLeft: "auto",
                      padding: "2px 8px",
                      borderRadius: 999,
                      background: "linear-gradient(180deg,#f2d783,#c9a84c 60%,#9a7530)",
                      color: "#1a1a19",
                      fontSize: "0.6rem",
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.4)",
                      border: "1px solid #7a5e2a",
                      textTransform: "uppercase",
                    }}
                  >
                    {t("dues.newBadge", "Nou")}
                  </span>
                )}
                {to === "/petitions" && pendingPetitions > 0 && (
                  <span
                    className="sidebar-new-badge"
                    data-testid="petitions-badge"
                    style={{
                      marginLeft: "auto",
                      padding: "2px 8px",
                      borderRadius: 999,
                      background: "linear-gradient(180deg,#f2d783,#c9a84c 60%,#9a7530)",
                      color: "#1a1a19",
                      fontSize: "0.6rem",
                      fontWeight: 700,
                      border: "1px solid #7a5e2a",
                    }}
                  >
                    {pendingPetitions}
                  </span>
                )}
              </NavLink>
            </React.Fragment>
          ))}

          {isAdmin && (!isLimitedOfficer || officerPerms.has("mobile_app")) && (
            <>
              <div className="sidebar-group">{t("nav.admin")}</div>
              {/* Users page is reserved for the obedience-level admin.
                  Child-lodge admins (lodge admins underneath an obedience)
                  no longer see this entry — they manage only their lodge
                  members through the Members page. */}
              {!user?.child_lodge_id && !isLimitedOfficer && (
                <NavLink
                  to="/users"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                  data-testid="nav-users"
                >
                  <UserCog size={16} strokeWidth={1.6} />
                  <span>{t("nav.users")}</span>
                </NavLink>
              )}
              {!isLimitedOfficer && (
                <NavLink
                  to="/audit-log"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                  data-testid="nav-audit-log"
                >
                  <FileText size={16} strokeWidth={1.6} />
                  <span>{t("nav.auditLog")}</span>
                </NavLink>
              )}
              {(!isLimitedOfficer || officerPerms.has("mobile_app")) && (
                <NavLink
                  to="/mobile-app"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                  data-testid="nav-mobile-app"
                >
                  <Smartphone size={16} strokeWidth={1.6} />
                  <span>{t("nav.mobileApp", "Aplicația Mobilă")}</span>
                </NavLink>
              )}
            </>
          )}
        </nav>

        <div className="gold-divider mx-6" />

        <div className="px-6 py-5">
          <div className="sidebar-user-name">{user?.name}</div>
          <div className="sidebar-user-role">
            {user?.role === "admin" ? t("common.administrator") : t("common.member")}
          </div>
          <button onClick={handleLogout} className="sidebar-logout" data-testid="logout-btn">
            <LogOut size={16} strokeWidth={1.6} />
            <span>{t("nav.logout")}</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-x-hidden">
        {/* Top bar: Lodge name + notifications + hamburger */}
        <div className="topbar">
          <div className="topbar-lodge">
            <CompassLogo size={26} color="#e8c97a" />
            <div>
              {lodge?.is_child_lodge && lodge?.parent_lodge?.name && (
                <div
                  className="topbar-lodge-breadcrumb"
                  data-testid="topbar-lodge-breadcrumb"
                  style={{
                    fontFamily: "Cinzel, serif",
                    fontSize: "0.62rem",
                    letterSpacing: "0.18em",
                    color: "#9a7530",
                    textTransform: "uppercase",
                    marginBottom: 2,
                  }}
                >
                  {lodge.parent_lodge.obedience_abbreviation || lodge.parent_lodge.name}
                  <span style={{ margin: "0 0.4rem", opacity: 0.7 }}>›</span>
                </div>
              )}
              <div className="topbar-lodge-name">
                {(lodge?.name || "SQUARE & COMPASS").toUpperCase()}
                {lodge?.number && <span className="topbar-lodge-ver"> {lodge.number}</span>}
              </div>
              {lodge?.city && <div className="topbar-lodge-city">{lodge.city}</div>}
            </div>
          </div>
          <div className="relative flex items-center gap-2 sm:gap-3">
            <LanguageSwitcher />
            {isAdmin && (transfers.incoming.length > 0 || transfers.outgoing.length > 0) && (
              <button
                className="notif-btn"
                onClick={() => setTransferInbox(true)}
                data-testid="transfers-btn"
                title={t("transfers.inboxTitle", "Cereri transfer")}
              >
                <Repeat2 size={18} strokeWidth={1.6} color="#e8c97a" />
                {transfers.incoming.length > 0 && (
                  <span className="notif-dot" data-testid="transfers-incoming-dot">
                    {transfers.incoming.length}
                  </span>
                )}
              </button>
            )}
            <button
              className="notif-btn"
              onClick={() => setNotifOpen((o) => !o)}
              data-testid="notifications-btn"
            >
              <Bell size={18} strokeWidth={1.6} color="#e8c97a" />
              {unread > 0 && <span className="notif-dot">{unread}</span>}
            </button>
            {notifOpen && (
              <div className="notif-panel" onMouseLeave={() => setNotifOpen(false)}>
                <div className="notif-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <span>{t("topbar.notifications")}</span>
                  {notifs.length > 0 && (
                    <button
                      type="button"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(t("topbar.clearNotifConfirm"))) return;
                        try {
                          await api.delete("/notifications/clear");
                          // The backend stamps a per-user cleared_at cutoff so
                          // these rows are now hidden from /api/notifications.
                          // Locally we empty the list so the panel reflects it
                          // immediately (the next poll will confirm).
                          setNotifs([]);
                          toast.success(t("topbar.clearNotifDone"));
                        } catch (err) {
                          toast.error(formatApiError(err));
                        }
                      }}
                      className="notif-clear-btn"
                      title={t("topbar.clearNotifTitle")}
                      data-testid="clear-notifs-btn"
                    >
                      {t("topbar.clearNotif")}
                    </button>
                  )}
                </div>
                {notifs.length === 0 ? (
                  <div className="notif-empty">{t("topbar.noNotifications")}</div>
                ) : (
                  notifs.map((n) => (
                    <button
                      key={n.id}
                      className={`notif-item ${!n.is_read ? "unread" : ""}`}
                      onClick={() => markRead(n.id)}
                    >
                      <div className="notif-title">{n.text}</div>
                      <div className="notif-meta">
                        {new Date(n.created_at).toLocaleString(t("appShell.en_gb"))} · {n.actor}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
            <button onClick={handleLogout} className="topbar-logout hidden lg:inline-flex" data-testid="topbar-logout">{t("nav.logout")}</button>
            {/* Hamburger — visible on mobile + tablet */}
            <button
              type="button"
              className="hamburger-btn lg:hidden"
              onClick={() => setSidebarOpen((o) => !o)}
              aria-label="Menu"
              data-testid="hamburger-btn"
            >
              {sidebarOpen ? <X size={20} color="#e8c97a" /> : <Menu size={20} color="#e8c97a" />}
            </button>
          </div>
        </div>

        {activeChildLodge && (
          <div
            data-testid="impersonation-banner"
            style={{
              background: "linear-gradient(90deg, #c9a84c 0%, #b89039 100%)",
              color: "#0d1b2a",
              padding: "0.5rem 1.2rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
              borderBottom: "1px solid #9a7530",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <LogIn size={14} />
              <span
                style={{
                  fontFamily: "Cinzel, serif",
                  letterSpacing: "0.18em",
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                }}
              >
                {t("jurisdiction.impersonatingPrefix", "Vizualizare ca lojă")}
              </span>
              <span
                style={{
                  fontFamily: "Cinzel, serif",
                  letterSpacing: "0.06em",
                  fontSize: "0.92rem",
                  fontWeight: 600,
                }}
                data-testid="impersonation-lodge-name"
              >
                {activeChildLodge.name}
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                exitChildLodge();
                navigate("/users");
              }}
              data-testid="exit-impersonation-btn"
              style={{
                background: "#0d1b2a",
                color: "#e8c97a",
                border: "1px solid #0d1b2a",
                padding: "0.35rem 0.85rem",
                fontFamily: "Cinzel, serif",
                letterSpacing: "0.16em",
                fontSize: "0.7rem",
                cursor: "pointer",
                textTransform: "uppercase",
              }}
            >
              {t("jurisdiction.exitImpersonation", "Înapoi la obediență")}
            </button>
          </div>
        )}

        {isAdmin && subStatus?.expires_at != null && subStatus.days_left <= 7 && (
          <div
            data-testid="subscription-warning-banner"
            style={{
              background: "#fdf3d7",
              color: "#6b4e0e",
              borderBottom: "1px solid #c9a84c",
              padding: "0.55rem 1.2rem",
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontFamily: "'Crimson Text', serif",
              fontSize: "0.95rem",
            }}
          >
            <Bell size={15} />
            <span>
              {t("subWarn.text", "Abonamentul {{scope}} expiră la {{date}} ({{count}} zile rămase). Contactați administratorul aplicației pentru reînnoire.", {
                scope: subStatus.scope === "lodge" ? t("subWarn.lodge", "lojii") : t("subWarn.obedience", "obedienței"),
                date: String(subStatus.expires_at).slice(0, 10),
                count: subStatus.days_left,
              })}
            </span>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-6 sm:py-10 fade-in app-content-wrap">
          <Outlet />
        </div>
      </main>
      {transferInbox && (
        <TransferInbox
          incoming={transfers.incoming}
          outgoing={transfers.outgoing}
          onClose={() => setTransferInbox(false)}
          onChange={loadTransfers}
          t={t}
          locale={t("appShell.en_gb")}
        />
      )}
    </div>
  );
}

function TransferInbox({ incoming, outgoing, onClose, onChange, t, locale }) {
  const [busy, setBusy] = React.useState(null);

  const decide = async (tr, action) => {
    setBusy(tr.id);
    try {
      await api.post(`/transfers/${tr.id}/${action}`, {});
      toast.success(action === "approve"
        ? t("transfers.approvedToast", "Transfer aprobat.")
        : t("transfers.rejectedToast", "Transfer respins."));
      await onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const fmt = (iso) => new Date(iso).toLocaleString(locale, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div
        className="dialog-panel"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 640, width: "100%" }}
        data-testid="transfer-inbox"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="dialog-title" style={{ margin: 0 }}>{t("transfers.inboxTitle", "Cereri transfer")}</h2>
          <button type="button" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="section-heading mb-2">{t("transfers.incomingHeading", "De aprobat (la noi)")}</div>
        {incoming.length === 0 ? (
          <p style={{ fontStyle: "italic", color: "#858481", fontSize: "0.85rem" }}>
            {t("transfers.empty", "Nicio cerere în așteptare.")}
          </p>
        ) : (
          <ul style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }} data-testid="transfer-incoming-list">
            {incoming.map((tr) => (
              <li
                key={tr.id}
                style={{ background: "#fdf4e0", border: "1px solid #c9a84c", padding: "0.7rem 0.9rem" }}
                data-testid={`transfer-item-${tr.id}`}
              >
                <div style={{ fontFamily: "Cinzel, serif", color: "#0d1b2a", fontSize: "0.95rem" }}>
                  {tr.member_first_name} {tr.member_last_name}
                </div>
                <div className="kv-label">
                  → {tr.target_lodge_name} · {fmt(tr.requested_at)}
                </div>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <button
                    className="btn-primary-gold"
                    onClick={() => decide(tr, "approve")}
                    disabled={busy === tr.id}
                    data-testid={`approve-transfer-${tr.id}`}
                  >
                    {busy === tr.id ? "..." : t("transfers.approve", "APROBĂ")}
                  </button>
                  <button
                    className="btn-danger"
                    onClick={() => decide(tr, "reject")}
                    disabled={busy === tr.id}
                    data-testid={`reject-transfer-${tr.id}`}
                  >
                    {t("transfers.reject", "RESPINGE")}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="gold-divider my-4" />
        <div className="section-heading mb-2">{t("transfers.outgoingHeading", "În așteptare (de la noi)")}</div>
        {outgoing.length === 0 ? (
          <p style={{ fontStyle: "italic", color: "#858481", fontSize: "0.85rem" }}>
            {t("transfers.empty", "Nicio cerere în așteptare.")}
          </p>
        ) : (
          <ul style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }} data-testid="transfer-outgoing-list">
            {outgoing.map((tr) => (
              <li
                key={tr.id}
                style={{ background: "#fff", border: "1px solid var(--cream-200)", padding: "0.7rem 0.9rem" }}
                data-testid={`transfer-out-${tr.id}`}
              >
                <div style={{ fontFamily: "Cinzel, serif", color: "#0d1b2a", fontSize: "0.95rem" }}>
                  {tr.member_first_name} {tr.member_last_name}
                </div>
                <div className="kv-label">
                  ← {tr.home_lodge_name} · {fmt(tr.requested_at)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
